import { useMemo } from 'react';

import classNames from 'classnames';
import { useShallow } from 'zustand/shallow';

import { Icon } from '@/shared/ui/Icon';

import {
  buildDagTableRows,
  countDagSummaryStatuses,
  filterDagTableRows,
} from '@/features/dag-list/model/dagListDashboardSelectors';
import { useDagListDashboardStore } from '@/features/dag-list/model/useDagListDashboardStore';

import styles from './DagListSummary.module.css';

const SUMMARY_STATUS_ITEMS = [
  {
    label: 'Draft',
    status: 'draft',
    iconName: 'minus-circle',
    iconColor: 'var(--opds-sem-color-onContainer-colorful-gray)',
    iconClassName: styles.gray,
  },
  {
    label: 'Failure',
    status: 'failure',
    iconName: 'cancel',
    iconColor: 'var(--opds-sem-color-onContainer-colorful-red)',
    iconClassName: styles.red,
  },
  {
    label: 'Success',
    status: 'success',
    iconName: 'check-circle',
    iconColor: 'var(--opds-sem-color-onContainer-colorful-emerald)',
    iconClassName: styles.green,
  },
  {
    label: 'Running',
    status: 'running',
    iconName: 'running-circle',
    iconColor: 'var(--opds-sem-color-onContainer-colorful-blue)',
    iconClassName: styles.blue,
  },
  {
    label: 'Pending',
    status: 'pending',
    iconName: 'pending',
    iconColor: 'var(--opds-sem-color-onContainer-colorful-orange)',
    iconClassName: styles.orange,
  },
] as const;

export const DagListSummary = () => {
  const { sourceItems, filters, selectedVersionByDagUuid, error } = useDagListDashboardStore(
    useShallow((state) => ({
      sourceItems: state.sourceItems,
      filters: state.filters,
      selectedVersionByDagUuid: state.selectedVersionByDagUuid,
      error: state.error,
    }))
  );

  if (error) throw error;

  const filteredRows = useMemo(() => {
    const tableRows = buildDagTableRows(sourceItems, selectedVersionByDagUuid);
    return filterDagTableRows(tableRows, filters);
  }, [filters, selectedVersionByDagUuid, sourceItems]);

  const statusCounts = useMemo(() => countDagSummaryStatuses(filteredRows), [filteredRows]);
  const summaryItems = [
    { ...SUMMARY_STATUS_ITEMS[0], value: statusCounts.draft },
    { ...SUMMARY_STATUS_ITEMS[1], value: statusCounts.failure },
    { ...SUMMARY_STATUS_ITEMS[2], value: statusCounts.success },
    { ...SUMMARY_STATUS_ITEMS[3], value: statusCounts.running },
    { ...SUMMARY_STATUS_ITEMS[4], value: statusCounts.pending },
  ] as const;

  return (
    <div className={styles.dashboard}>
      <div className={styles.total}>
        <span className={styles.totalLabel}>Dag Total</span>
        <span className={styles.totalValue}>{filteredRows.length.toLocaleString()}</span>
      </div>
      <div className={styles.whiteSpace}>
        {summaryItems.map((item) => (
          <div key={item.label} className={styles.whiteSpaceItem}>
            <div className={classNames(styles.whiteSpaceItemIcon, item.iconClassName)}>
              <Icon name={item.iconName} width={32} height={32} color={item.iconColor} />
            </div>
            <div className={styles.whiteSpaceItemContent}>
              <span className={styles.whiteSpaceLabel}>{item.label}</span>
              <span className={styles.whiteSpaceValue}>{item.value.toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
